import { additionalDataDocumentsForm } from 'apps/Onboarding/views/BlueBook/components/Documents/components/DocumentsForm/interface'
import {
  bytesInMb,
  EFileClassification,
  HTTP_RESPONSE_STATUS_BAD_REQUEST,
  HTTP_RESPONSE_STATUS_CODE_STRING,
  HTTP_RESPONSE_STATUS_SERVICE_UNAVAILABLE
} from 'utils/constants'
import { formatDate } from 'utils/date'
import { getCustomMaxSize } from '../../helpers'
import { getFileTypes } from 'apps/Onboarding/views/BlueBook/helpers'
import { IActivationsPlansFiles, IWizardData } from 'context/interface'
import {
  IDENTIFICATION_REVERSE_FILE_ID,
  MAX_FILE_NAME_CHARS,
  NO_REQUIRED_DOCUMENTS_BY_MCC,
  parameterMap,
  T_ROOT
} from '../../constants'
import { IDocumentsForm, IDocumentsFormInput, IFileItem, IFormSubmitErrors, ITaxRegimeOptions } from '../../interface'
import { showOnboardingErrorToastAlert } from 'apps/Onboarding/helpers'
import { TFunction } from 'i18next'
import { ValidationErrors } from 'final-form'

export const T_ROOT_ERRORS = `${T_ROOT}.file_uploaders.errors`

const checkIfHasDocumentsError = (
  error: any,
  SUBMIT_ERRORS: IFormSubmitErrors,
  t: TFunction
): IFormSubmitErrors | void => {
  const { errors } = error.response.data
  errors.forEach(({ code, parameter }: { code: string; parameter: string }) => {
    if (code === 'fileId') {
      SUBMIT_ERRORS[parameter] = getMessageError(parameter, t)
    }
  })

  return SUBMIT_ERRORS
}

export const getDocumentsFormError = (
  error: any,
  SUBMIT_ERRORS: IFormSubmitErrors,
  setHasKycProblem: React.Dispatch<React.SetStateAction<boolean>>,
  setAdditionalData: React.Dispatch<React.SetStateAction<additionalDataDocumentsForm>>,
  t: TFunction
): IFormSubmitErrors | void => {
  if (error.response && error.response.data) {
    if (
      error.response.data.responseCode === HTTP_RESPONSE_STATUS_CODE_STRING[HTTP_RESPONSE_STATUS_BAD_REQUEST] &&
      error.response.data.errors.length > 0
    ) {
      return checkIfHasDocumentsError(error, SUBMIT_ERRORS, t)
    }
    if (
      error.response.data.responseCode === HTTP_RESPONSE_STATUS_CODE_STRING[HTTP_RESPONSE_STATUS_SERVICE_UNAVAILABLE] &&
      error?.response?.data?.errors
    ) {
      const { errors } = error.response.data
      const additionalData: additionalDataDocumentsForm = {
        fiscal: false,
        legal: false
      }
      errors.forEach((error: any) => {
        const propertyName = parameterMap[error.parameter]
        if (propertyName) {
          additionalData[propertyName] = true
        }
      })
      setAdditionalData(additionalData)
      return setHasKycProblem(true)
    }
  }
  return showOnboardingErrorToastAlert(t)
}

export const getMessageError = (id: string, t: TFunction): string => {
  if (id === EFileClassification.officialIDFront) {
    return t(`${T_ROOT_ERRORS}.couldNotBeUploadedIDFront`)
  }

  if (id === EFileClassification.officialIDBack) {
    return t(`${T_ROOT_ERRORS}.couldNotBeUploadedIDBack`)
  }

  return t(`${T_ROOT_ERRORS}.couldNotBeUploaded`)
}

export const validateFileType = (
  ERRORS: Record<string, string>,
  t: TFunction,
  fileTypes: { [key: string]: string[] },
  FILE: IFileItem,
  fileName: string
): void => {
  const file_types = getFileTypes(fileName, fileTypes)
  if (FILE && Object.keys(file_types).length > 0 && !Object.keys(file_types).includes(FILE.type)) {
    ERRORS[fileName] = t(`${T_ROOT_ERRORS}.file-invalid-type`)
  }
}

export const validateFileUpladError = (
  ERRORS: Record<string, string>,
  t: TFunction,
  FILE: IFileItem,
  fileName: string
): void => {
  if (FILE && FILE.hasError) {
    ERRORS[fileName] = t(`${T_ROOT_ERRORS}.field_upload_error`)
  }
}

export const validateMaxSize = (
  ERRORS: Record<string, string>,
  t: TFunction,
  fileMaxSizeAllowed: number,
  FILE: IFileItem,
  fileName: string
): void => {
  const maxSize = getCustomMaxSize(fileName, fileMaxSizeAllowed)
  const sizeToMb = maxSize / bytesInMb
  if (FILE?.size > maxSize) {
    ERRORS[fileName] = t(`${T_ROOT_ERRORS}.file-too-large`, { size: sizeToMb })
  }
}

export const validateOptionalList = (
  ERRORS: Record<string, string>,
  t: TFunction,
  files: Array<IActivationsPlansFiles>,
  wizardData: IWizardData,
  FILE: IFileItem,
  fileName: string,
  hasInternationalDoc: boolean
): void => {
  const IS_IDENTIFICATION_REVERSE_FILE: boolean = fileName === IDENTIFICATION_REVERSE_FILE_ID
  const IS_RESUBMITTED_FILE: boolean = files.some(
    ({ fileClassificationId, reason }: IActivationsPlansFiles) => fileClassificationId === fileName && reason
  )
  const OPTIONAL_LIST = NO_REQUIRED_DOCUMENTS_BY_MCC[wizardData.mcc] || []
  const IS_IDENTIFICATION_REVERSE_WITHOUT_INTERNATIONAL_DOC = IS_IDENTIFICATION_REVERSE_FILE && !hasInternationalDoc

  if (!OPTIONAL_LIST.includes(fileName)) {
    if (!FILE) {
      if (
        IS_RESUBMITTED_FILE ||
        !IS_IDENTIFICATION_REVERSE_FILE ||
        IS_IDENTIFICATION_REVERSE_WITHOUT_INTERNATIONAL_DOC
      ) {
        ERRORS[fileName] = t(`${T_ROOT_ERRORS}.this_file_is_required`)
      }
    }
  }
}

export const validateFileName = (
  ERRORS: Record<string, string>,
  t: TFunction,
  FILE: IFileItem,
  fileName: string
): void => {
  const FILE_NAME = FILE?.name || ''

  if (FILE_NAME.length > MAX_FILE_NAME_CHARS) {
    ERRORS[fileName] = t(`${T_ROOT_ERRORS}.max_chars`) + MAX_FILE_NAME_CHARS.toString()
  }
}

export const validateManualInfoFields = (
  values: Record<string, string | IFileItem>,
  ERRORS: Record<string, string>,
  t: TFunction,
  field: string
): void => {
  if (!values[field]) {
    ERRORS[field] = t(`${T_ROOT_ERRORS}.field_required`)
  }
}

export const validationField = (
  values: Record<string, string | IFileItem>,
  ERRORS: Record<string, string>,
  fileName: string,
  files: Array<IActivationsPlansFiles>,
  fileTypes: { [key: string]: string[] },
  fileMaxSizeAllowed: number,
  hasInternationalDoc: boolean,
  t: TFunction,
  wizardData: IWizardData
): ValidationErrors => {
  const FILE: IFileItem = values[fileName] as IFileItem

  validateFileType(ERRORS, t, fileTypes, FILE, fileName)
  validateFileUpladError(ERRORS, t, FILE, fileName)
  validateMaxSize(ERRORS, t, fileMaxSizeAllowed, FILE, fileName)
  validateOptionalList(ERRORS, t, files, wizardData, FILE, fileName, hasInternationalDoc)
  validateFileName(ERRORS, t, FILE, fileName)

  return ERRORS
}

export const formatTaxRegimesOptions = (
  rawTaxRegimesOptions: { id: string; code: string; description: string }[]
): ITaxRegimeOptions[] => {
  const rawTaxRegimes = rawTaxRegimesOptions?.reduce(
    (obj: Record<string, string>, { code, description }: Record<string, string>) => ({
      ...obj,
      [code]: description
    }),
    {}
  )
  return Object.entries(rawTaxRegimes).map(regime => ({
    label: regime[1] as string,
    value: regime[0]
  }))
}

export const buildData = (values: IDocumentsFormInput): IDocumentsForm => {
  const data: IDocumentsForm = {}
  if (values.legalRepresentativeBirthdate || values.legalRepresentativeName) {
    data.legal = {
      legalRepresentativeBirthdate: getRepresentativeBirthdate(values.legalRepresentativeBirthdate as string),
      legalRepresentativeName: values.legalRepresentativeName
    }
  }
  if (values.taxPayerRegister || values.taxRegime || values.commercialLegalName || values.street) {
    data.fiscal = {
      commercialLegalName: values.commercialLegalName,
      externalNumber: values.externalNumber,
      interiorNumber: values.interiorNumber,
      municipality: values.municipality,
      neighborhood: values.neighborhood,
      state: values.state,
      street: values.street,
      taxPayerRegister: getTaxPayerRegister(values.taxPayerRegister as string),
      taxRegime: getTaxRegime(values.taxRegime),
      zip: values.zip
    }
  }
  return data
}

const getRepresentativeBirthdate = (legalRepresentativeBirthdate: string): string => {
  const representativeBirthdateDate = new Date(legalRepresentativeBirthdate as string)
  return formatDate(representativeBirthdateDate, 'dd-MM-yyyy')
}

const getTaxPayerRegister = (taxPayerRegister: string): string => {
  return taxPayerRegister.toString().toUpperCase()
}

const getTaxRegime = (taxRegime: any): string => {
  return taxRegime.value
}
